import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  GitBranch, 
  Mail, 
  MousePointer,
  Eye,
  EyeOff,
  MessageSquare,
  Play,
  Pause,
  Trash2,
  Clock,
  ChevronRight,
  Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sequences = () => {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [newSequence, setNewSequence] = useState({
    name: '',
    stop_on_reply: true,
    steps: []
  });

  useEffect(() => {
    fetchSequences();
  }, []);

  const fetchSequences = async () => {
    try {
      const response = await axios.get(`${API}/sequences`);
      setSequences(response.data);
    } catch (error) {
      toast.error('Failed to fetch sequences');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSequence = async () => {
    if (!newSequence.name) {
      toast.error('Please enter a sequence name');
      return;
    }
    try {
      await axios.post(`${API}/sequences`, newSequence);
      toast.success('Sequence created');
      setShowBuilder(false);
      setNewSequence({ name: '', stop_on_reply: true, steps: [] });
      fetchSequences();
    } catch (error) {
      toast.error('Failed to create sequence');
    }
  };

  const addStep = () => {
    const stepNumber = newSequence.steps.length + 1;
    setNewSequence({
      ...newSequence,
      steps: [...newSequence.steps, {
        step_number: stepNumber,
        step_type: 'email',
        delay_days_if_opened: 5,
        delay_days_if_not_opened: 7,
        delay_days_if_clicked: 3,
        prompt_modifier: '',
        subject_line_rule: 'new_signal',
        tone_modifier: 'maintain'
      }]
    });
  };

  const updateStep = (index, field, value) => {
    const updated = [...newSequence.steps];
    updated[index] = { ...updated[index], [field]: value };
    setNewSequence({ ...newSequence, steps: updated });
  };

  const removeStep = (index) => {
    const updated = newSequence.steps.filter((_, i) => i !== index);
    updated.forEach((step, i) => step.step_number = i + 1);
    setNewSequence({ ...newSequence, steps: updated });
  };

  const toggleSequenceStatus = async (sequence) => {
    try {
      const newStatus = sequence.status === 'active' ? 'paused' : 'active';
      await axios.patch(`${API}/sequences/${sequence.id}`, { status: newStatus });
      toast.success(`Sequence ${newStatus}`);
      fetchSequences();
    } catch (error) {
      toast.error('Failed to update sequence');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sequences-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-serif text-4xl text-white mb-2">Sequences</h1>
          <p className="text-slate-400">
            Automated follow-up sequences with AI-powered personalization
          </p>
        </div>
        <Dialog open={showBuilder} onOpenChange={setShowBuilder}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 glow-blue btn-press" data-testid="create-sequence-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Sequence
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-white">Create Sequence</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 mt-4">
              {/* Sequence Name */}
              <div className="space-y-2">
                <Label className="text-slate-300">Sequence Name</Label>
                <Input
                  value={newSequence.name}
                  onChange={(e) => setNewSequence({...newSequence, name: e.target.value})}
                  className="bg-slate-950 border-slate-800"
                  placeholder="Brand Studio Intro"
                  data-testid="sequence-name"
                />
              </div>

              {/* Settings */}
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="text-sm text-white">Stop on Reply</p>
                  <p className="text-xs text-slate-500">Pause sequence when lead replies</p>
                </div>
                <Switch
                  checked={newSequence.stop_on_reply}
                  onCheckedChange={(v) => setNewSequence({...newSequence, stop_on_reply: v})}
                  data-testid="stop-on-reply"
                />
              </div>

              {/* Steps */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">Steps</Label>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={addStep}
                    className="border-slate-700"
                    data-testid="add-step-btn"
                    disabled={newSequence.steps.length >= 3}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Step
                  </Button>
                </div>

                {newSequence.steps.length === 0 ? (
                  <div className="p-8 border border-dashed border-slate-700 rounded-lg text-center">
                    <GitBranch className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                    <p className="text-slate-500">No steps yet. Add your first step.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {newSequence.steps.map((step, index) => (
                      <Card key={index} className="bg-slate-800/50 border-slate-700">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-xs font-mono text-white">
                                {step.step_number}
                              </div>
                              <span className="text-sm font-medium text-white">Step {step.step_number}</span>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => removeStep(index)}
                              className="text-slate-500 hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <Label className="text-xs text-slate-400">Type</Label>
                              <Select 
                                value={step.step_type}
                                onValueChange={(v) => updateStep(index, 'step_type', v)}
                              >
                                <SelectTrigger className="bg-slate-900 border-slate-700">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="email">Email</SelectItem>
                                  <SelectItem value="linkedin_message">LinkedIn Message</SelectItem>
                                  <SelectItem value="linkedin_connect">LinkedIn Connect</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs text-slate-400">Tone</Label>
                              <Select 
                                value={step.tone_modifier}
                                onValueChange={(v) => updateStep(index, 'tone_modifier', v)}
                              >
                                <SelectTrigger className="bg-slate-900 border-slate-700">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="maintain">Maintain</SelectItem>
                                  <SelectItem value="softer">Softer</SelectItem>
                                  <SelectItem value="more_direct">More Direct</SelectItem>
                                  <SelectItem value="break_up">Break Up</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs text-slate-400">Subject Rule</Label>
                              <Select 
                                value={step.subject_line_rule}
                                onValueChange={(v) => updateStep(index, 'subject_line_rule', v)}
                              >
                                <SelectTrigger className="bg-slate-900 border-slate-700">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="new_signal">New Signal</SelectItem>
                                  <SelectItem value="re_reference_signal">Re-reference</SelectItem>
                                  <SelectItem value="no_subject_change">No Change</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {/* Delay Settings */}
                          <div className="p-3 bg-slate-900/50 rounded-lg space-y-3">
                            <p className="text-xs font-mono text-slate-500 uppercase">Delay Rules</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                              <div className="flex items-center gap-2">
                                <Eye className="w-4 h-4 text-emerald-400" />
                                <Input
                                  type="number"
                                  value={step.delay_days_if_opened}
                                  onChange={(e) => updateStep(index, 'delay_days_if_opened', parseInt(e.target.value))}
                                  className="bg-slate-800 border-slate-700 h-8 w-16"
                                />
                                <span className="text-xs text-slate-500">days if opened</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <EyeOff className="w-4 h-4 text-slate-500" />
                                <Input
                                  type="number"
                                  value={step.delay_days_if_not_opened}
                                  onChange={(e) => updateStep(index, 'delay_days_if_not_opened', parseInt(e.target.value))}
                                  className="bg-slate-800 border-slate-700 h-8 w-16"
                                />
                                <span className="text-xs text-slate-500">days if not</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <MousePointer className="w-4 h-4 text-blue-400" />
                                <Input
                                  type="number"
                                  value={step.delay_days_if_clicked}
                                  onChange={(e) => updateStep(index, 'delay_days_if_clicked', parseInt(e.target.value))}
                                  className="bg-slate-800 border-slate-700 h-8 w-16"
                                />
                                <span className="text-xs text-slate-500">days if clicked</span>
                              </div>
                            </div>
                          </div>

                          {/* Prompt Modifier */}
                          <div className="space-y-2">
                            <Label className="text-xs text-slate-400 flex items-center gap-1">
                              <Sparkles className="w-3 h-3 text-violet-400" />
                              AI Prompt Modifier
                            </Label>
                            <Textarea
                              value={step.prompt_modifier}
                              onChange={(e) => updateStep(index, 'prompt_modifier', e.target.value)}
                              className="bg-slate-900 border-slate-700 text-sm h-20"
                              placeholder="Reference what they engaged with. End with an open question."
                            />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
                <Button variant="ghost" onClick={() => setShowBuilder(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateSequence}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-sequence-btn"
                >
                  Create Sequence
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Sequences List */}
      {loading ? (
        <div className="grid gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-slate-800/50 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : sequences.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="py-12 text-center">
            <GitBranch className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No sequences yet</h3>
            <p className="text-slate-500 mb-4">Create your first sequence to automate follow-ups</p>
            <Button onClick={() => setShowBuilder(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Sequence
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {sequences.map((sequence) => (
            <Card 
              key={sequence.id} 
              className="bg-slate-900/50 border-slate-800 card-hover"
              data-testid={`sequence-${sequence.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      sequence.status === 'active' ? 'bg-emerald-500/10' : 'bg-slate-800'
                    }`}>
                      <GitBranch className={`w-6 h-6 ${
                        sequence.status === 'active' ? 'text-emerald-400' : 'text-slate-500'
                      }`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-white">{sequence.name}</h3>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {sequence.steps?.length || 0} steps
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {sequence.stats?.enrolled_count || 0} enrolled
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right mr-4">
                      <p className="text-2xl font-mono text-white">{sequence.stats?.reply_rate || 0}%</p>
                      <p className="text-xs text-slate-500">Reply Rate</p>
                    </div>
                    <Badge 
                      variant="outline" 
                      className={`${
                        sequence.status === 'active' 
                          ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                          : 'bg-slate-800 text-slate-400 border-slate-700'
                      }`}
                    >
                      {sequence.status}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleSequenceStatus(sequence)}
                      className="text-slate-400 hover:text-white"
                      data-testid={`toggle-sequence-${sequence.id}`}
                    >
                      {sequence.status === 'active' ? (
                        <Pause className="w-4 h-4" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </Button>
                    <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Steps Preview */}
                {sequence.steps?.length > 0 && (
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-800">
                    {sequence.steps.map((step, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 rounded-lg">
                          <div className="w-5 h-5 rounded-full bg-blue-600/20 flex items-center justify-center">
                            <span className="text-xs font-mono text-blue-400">{step.step_number}</span>
                          </div>
                          <span className="text-xs text-slate-400 capitalize">
                            {step.step_type.replace('_', ' ')}
                          </span>
                        </div>
                        {i < sequence.steps.length - 1 && (
                          <ChevronRight className="w-4 h-4 text-slate-600" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Sequences;
